import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout';
import ContentBox from '../../../components/ContentBox';
import { getMaintenanceStatus, setCloudUploadMode as apiSetMode, getAdminCloudImages, adminDeleteCloudImage } from '../../../services/api';
import { toast } from 'react-toastify';

const API_ORIGIN = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '').replace(/\/$/, '');

const resolveUploadSrc = (src) => {
    if (!src || src.startsWith('data:') || src.startsWith('http')) return src;
    return `${API_ORIGIN}${src.startsWith('/') ? '' : '/'}${src}`;
};

const MODES = [
    {
        value: 'all',
        label: 'Uploads Online',
        activeBg: 'bg-green-600',
        activeText: 'text-white',
        inactiveBg: 'bg-gray-200',
        inactiveText: 'text-gray-800',
        description: 'All logged-in users can upload images to FranklyCloud.',
    },
    {
        value: 'staff_only',
        label: 'Uploads (Staff Only)',
        activeBg: 'bg-orange-500',
        activeText: 'text-white',
        inactiveBg: 'bg-gray-200',
        inactiveText: 'text-gray-800',
        description: 'Only users with the STF or OCC achievement can upload. All others see the upload option disabled.',
    },
    {
        value: 'offline',
        label: 'Uploads Offline',
        activeBg: 'bg-red-600',
        activeText: 'text-white',
        inactiveBg: 'bg-gray-200',
        inactiveText: 'text-gray-800',
        description: 'File uploads are disabled for all users. The upload option is disabled in FranklyCloud for everyone.',
    },
];

const PAGE_SIZE = 40;

function CloudUploadAdmin({ userData }) {
    const [currentMode, setCurrentMode] = useState('all');
    const [modeLoading, setModeLoading] = useState(true);
    const [modeUpdating, setModeUpdating] = useState(false);

    const [images, setImages] = useState([]);
    const [imagesLoading, setImagesLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);

    const [lightboxSrc, setLightboxSrc] = useState(null);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        getMaintenanceStatus()
            .then(r => { if (r?.cloudUploadMode) setCurrentMode(r.cloudUploadMode); })
            .catch(() => toast.error('Failed to load upload mode'))
            .finally(() => setModeLoading(false));
    }, []);

    const loadImages = useCallback(async (page) => {
        setImagesLoading(true);
        try {
            const res = await getAdminCloudImages(page);
            if (res?.success) {
                setImages(prev => page === 1 ? (res.images || []) : [...prev, ...(res.images || [])]);
                setTotalPages(res.pagination?.totalPages || 1);
                setTotal(res.pagination?.total || 0);
                setCurrentPage(page);
            }
        } catch {
            toast.error('Failed to load images');
        } finally {
            setImagesLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!modeLoading) loadImages(1);
    }, [modeLoading, loadImages]);

    const handleSetMode = async (mode) => {
        if (mode === currentMode || modeUpdating) return;
        setModeUpdating(true);
        try {
            const res = await apiSetMode(mode);
            if (res?.success) {
                setCurrentMode(mode);
                const m = MODES.find(m => m.value === mode);
                toast.success(`Upload mode set to: ${m?.label}`);
            }
        } catch {
            toast.error('Failed to update upload mode');
        } finally {
            setModeUpdating(false);
        }
    };

    const handleDelete = async (image) => {
        setDeleting(true);
        try {
            await adminDeleteCloudImage(image.id);
            setImages(prev => prev.filter(i => i.id !== image.id));
            setTotal(prev => Math.max(0, prev - 1));
            setPendingDelete(null);
            toast.success('Image deleted');
        } catch {
            toast.error('Failed to delete image');
        } finally {
            setDeleting(false);
        }
    };

    const handleLightboxClose = () => setLightboxSrc(null);

    useEffect(() => {
        if (!lightboxSrc) return;
        const onKey = (e) => { if (e.key === 'Escape') handleLightboxClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [lightboxSrc]);

    if (!userData || !userData.hasAdminRole) {
        return <Navigate to="/" replace />;
    }

    const activeModeInfo = MODES.find(m => m.value === currentMode);

    return (
        <>
            <AdminLayout>
                <div className="flex flex-col gap-4">
                    <ContentBox theme="default" tint="red" header="Cloud Upload Allowance">
                        {modeLoading ? (
                            <p className="text-sm text-gray-500">Loading...</p>
                        ) : (
                            <>
                                <p className="mb-4 text-sm">
                                    Current mode:{' '}
                                    <span className="font-bold">
                                        {activeModeInfo?.label}
                                    </span>
                                </p>

                                <div className="flex flex-col gap-2 mb-4">
                                    {MODES.map(mode => {
                                        const isActive = currentMode === mode.value;
                                        return (
                                            <button
                                                key={mode.value}
                                                onClick={() => handleSetMode(mode.value)}
                                                disabled={modeUpdating || isActive}
                                                className={`px-4 py-3 text-left font-['Jersey_10'] uppercase text-[16px] tracking-[1px] transition-all
                                                    ${isActive
                                                        ? `${mode.activeBg} ${mode.activeText} cursor-default`
                                                        : `${mode.inactiveBg} ${mode.inactiveText} hover:bg-gray-300`
                                                    }
                                                    ${modeUpdating && !isActive ? 'opacity-50 cursor-not-allowed' : ''}
                                                `}
                                                style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.2)' }}
                                            >
                                                {mode.label}
                                                {isActive && ' ✓'}
                                            </button>
                                        );
                                    })}
                                </div>

                                {activeModeInfo && (
                                    <p className="text-sm text-gray-600 border-t border-black/10 pt-3">
                                        {activeModeInfo.description}
                                    </p>
                                )}
                            </>
                        )}
                    </ContentBox>

                    <ContentBox theme="default" tint="red" header={`Uploaded Images${total > 0 ? ` (${total})` : ''}`}>
                        {imagesLoading && images.length === 0 ? (
                            <p className="text-sm text-gray-500">Loading images...</p>
                        ) : images.length === 0 ? (
                            <p className="text-sm text-gray-500">No user-uploaded images found.</p>
                        ) : (
                            <>
                                <div className="grid grid-cols-4 gap-3 mb-4">
                                    {images.map(image => (
                                        <div key={image.id} className="relative group border border-black/20 bg-black/5 overflow-hidden">
                                            <div
                                                className="aspect-square flex items-center justify-center overflow-hidden bg-black/10 cursor-pointer hover:opacity-90 transition-opacity"
                                                onClick={() => setLightboxSrc(resolveUploadSrc(image.src))}
                                            >
                                                <img
                                                    src={resolveUploadSrc(image.src)}
                                                    alt={image.name || 'Cloud image'}
                                                    className="w-full h-full object-contain"
                                                    loading="lazy"
                                                />
                                            </div>
                                            <div className="px-1.5 py-1 border-t border-black/10 bg-offwhite">
                                                <p className="text-[11px] text-coal/70 truncate leading-tight font-bold">{image.username}</p>
                                                <p className="text-[10px] text-coal/40 truncate">{image.name || '(unnamed)'}</p>
                                            </div>

                                            {pendingDelete !== image.id ? (
                                                <button
                                                    type="button"
                                                    onClick={() => setPendingDelete(image.id)}
                                                    className="absolute top-1 right-1 bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
                                                >
                                                    ✕
                                                </button>
                                            ) : (
                                                <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center gap-2 p-2">
                                                    <p className="text-white text-xs text-center font-bold">Delete this image?</p>
                                                    <div className="flex gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDelete(image)}
                                                            disabled={deleting}
                                                            className="px-2 py-1 bg-red-600 text-white text-xs font-bold uppercase hover:bg-red-700 disabled:opacity-50"
                                                        >
                                                            Delete
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setPendingDelete(null)}
                                                            className="px-2 py-1 bg-offwhite text-coal text-xs font-bold uppercase hover:bg-gray-200"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {currentPage < totalPages && (
                                    <button
                                        onClick={() => loadImages(currentPage + 1)}
                                        disabled={imagesLoading}
                                        className="px-4 py-2 bg-coal text-offwhite text-sm font-bold uppercase tracking-wide hover:bg-coal/80 disabled:opacity-50 transition-colors"
                                        style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.3)' }}
                                    >
                                        {imagesLoading ? 'Loading...' : 'Load More'}
                                    </button>
                                )}
                            </>
                        )}
                    </ContentBox>
                </div>
            </AdminLayout>

            {lightboxSrc && (
                <div
                    className="fixed inset-0 z-[10000] bg-black/90 flex items-center justify-center"
                    onClick={handleLightboxClose}
                >
                    <img
                        src={lightboxSrc}
                        alt=""
                        className="max-w-[90vw] max-h-[90vh] object-contain"
                        onClick={(e) => e.stopPropagation()}
                    />
                    <button
                        type="button"
                        onClick={handleLightboxClose}
                        className="absolute top-4 right-4 text-white/70 hover:text-white text-2xl leading-none w-8 h-8 flex items-center justify-center"
                    >
                        ✕
                    </button>
                </div>
            )}
        </>
    );
}

export default CloudUploadAdmin;
